import React, { useState, useEffect } from "react";
import "./style.css";
import { Col, message, Row, Skeleton, Spin } from "antd"; // Import Skeleton for loading state
import { LoadingOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom"; // Import Link for user management button
import CustomButtonWithIcon from "../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import CustomPrimaryButton from "../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import { dentalIcons } from "../../../dentail-indetifcation/utils-icons/dentalIcons";
import {
  errorRequestHandel,
  formateDateAndTime,
  normalizeSubscription,
  renderSubscriptionStatusTextAndBgColor,
  toTitleCase,
} from "../../../helper";
import CustomModal from "../../../age-assessment/components/modals/CustomModal";
import CancelationModal from "../cancelation/CancelationModal";
import SeatsProgressBar from "../seats-progress/SeatsProgressBar";
import { useSelector } from "react-redux";
import StripePricingTable from "../../strip/StripePricingTable";
import CancelledTag from "../cancelled-tag/CancelledTag"; // Import the tag
import useIsAdmin from "../../../hooks/useIsAdmin";
import {
  getUpgradeSeatsInvoice,
  increaseSeatsForMyOrganization,
  onCancelCurrentSubscriptionPlan,
} from "../../../components/https/planSubscriptions";
// Import the updated helper function

import { getSubscriptionState } from "../../../utils/subscriptionUtils"; // Import the updated utility function
import UpgradePanModal from "../upgrade-plan-modal/UpgradePanModal";
import DentalIDPricingTable from "../dental-id-price-table/DentalIDPricingTable";

const ActiveSubscription = ({ onRefetch }) => {
  const isAdmin = useIsAdmin();
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isUpdateSeats, setisUpdateSeats] = useState(false);
  const [isPricingTableOpen, setIsPricingTableOpen] = useState(false);
  const [isDentalIdPricingTableOpen, setIsDentalIdPricingTableOpen] =
    useState(false);
  const [upgradePlanTableOpen, setUpgradePlanTableOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seatsLoading, setSeatsLoading] = useState(false);
  const [seatsInfo, setSeatsInfo] = useState(null);
  const [chargesPerSeat, setChargesPerSeat] = useState(0);
  const myOrganization = useSelector((state) => state.general?.myOrganization);

  const normalizedSub = normalizeSubscription(
    myOrganization?.active_subscription
  );

  const subscriptionState = getSubscriptionState(normalizedSub);

  const totalSeats = normalizedSub?.noOfSeats ?? 0;
  const activeSeats =
    myOrganization?.examiners?.filter((examiner) => examiner.is_active)
      .length ?? 0;
  const availableSeats = totalSeats - activeSeats;
  const [seats, setSeats] = useState(0);

  const isUserAlreadyOnStripe = myOrganization?.subscriptions.some(
    (sub) => sub.type === "stripe"
  );

  useEffect(() => {
    setSeats(totalSeats);
  }, [isUpdateSeats, isDentalIdPricingTableOpen]);

  useEffect(() => {
    if (isUpdateSeats) {
      fetchSeatsInvoices();
    }
  }, [seats, isUpdateSeats]);

  const fetchSeatsInvoices = async () => {
    try {
      setSeatsLoading(true);
      const response = await getUpgradeSeatsInvoice({ seats: seats });
      if (response.status === 200) {
        setSeatsInfo(response.data?.data);
        const _chargesPerSeat = (
          response?.data?.data?.next_invoice_estimate /
          100 /
          seats
        ).toFixed(2);
        setChargesPerSeat(_chargesPerSeat);
        setSeatsLoading(false);
      }
    } catch (error) {
      setSeatsLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  const onConfirm = async () => {
    setLoading(true);
    try {
      const response = await onCancelCurrentSubscriptionPlan({
        id: normalizedSub?.id,
      });
      if (response.status === 200) {
        message.success("Your subscription has been cancelled successfully");
        onRefetch();
        setIsCancelModalVisible(false);
      }
    } catch (error) {
      errorRequestHandel({ error: error });
    } finally {
      setLoading(false);
      setIsCancelModalVisible(false);
    }
  };

  const onUpdateSeats = async () => {
    setLoading(true);
    try {
      const payload = { no_of_seats: seats };
      const response = await increaseSeatsForMyOrganization({ payload });
      if (response.status === 200) {
        message.success("Seats have been updated successfully");
        setisUpdateSeats(false);
        onRefetch();
      }
    } catch (error) {
      errorRequestHandel({ error: error });
    } finally {
      setLoading(false);
    }
  };

  const statusObject = renderSubscriptionStatusTextAndBgColor(
    normalizedSub?.status
  );
  const increaseSeats = () => {
    setSeats((prev) => prev + 1);
  };

  const decreaseSeats = () => {
    if (seats <= activeSeats) {
      message.warning("Please remove users before reducing seats.");
      return;
    }

    setSeats((prev) => {
      // Make sure not to reduce below active users count
      if (prev - 1 < activeSeats) {
        message.warning(
          "You cannot reduce seats below the number of active users. If you want to reduce seats please remove user first"
        );
        return prev;
      }
      return prev > 1 ? prev - 1 : 1;
    });
  };

  const nextInvoiceEstimate = (seatsInfo?.next_invoice_estimate / 100).toFixed(
    2
  );
  const currentProrationChargeNow = (
    seatsInfo?.current_proration_charge_now / 100
  ).toFixed(2);

  // Per-seat price
  const unit_price = (
    (seatsInfo?.base_next_cycle_amount || seatsInfo?.next_cycle_amount) /
    seatsInfo?.new_quantity /
    100
  ).toFixed(2);

  // Credit (only downgrades)
  const credit_applied_next_cycle = (
    seatsInfo?.credit_applied_next_cycle / 100
  ).toFixed(2);

  // Next cycle renewal (after credit if downgrade, or full amount if upgrade)
  const total_due_next_cycle = (seatsInfo?.total_due_next_cycle / 100).toFixed(
    2
  );

  // Immediate charge (only upgrades)
  const pay_now_amount = (seatsInfo?.pay_now_amount / 100).toFixed(2);

  return (
    <div className="active_subscription_container">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={12} xl={16}>
          <div className="active_subscription_body">
            <div className="active_subscription_body_section">
              <div className="active_subscription_body_heading">
                <p className="_title">{toTitleCase(normalizedSub?.type)}</p>
              </div>
              {/* Display a cancelled tag if applicable */}
              {/* {normalizedSub?.isCancelled && <CancelledTag />} */}
              <div className="active_subscription_plan">
                <p className="_title">{toTitleCase(normalizedSub?.type)}</p>
              </div>
            </div>
            <div className="active_subscription_info_section">
              <div className="_status_section">
                <p className="key">Status</p>
                {normalizedSub && (
                  <div
                    className="status_with_dot"
                    style={{ background: statusObject.bgColor }}
                  >
                    <div
                      className="_dot"
                      style={{ background: statusObject.textColor }}
                    />
                    <p style={{ color: statusObject.textColor }}>
                      {toTitleCase(normalizedSub.status)}
                    </p>
                  </div>
                )}
              </div>
              <div className="_status_section">
                <p className="key">{subscriptionState.dateLabel}</p>
                {normalizedSub?.endDate && (
                  <p className="value">
                    {formateDateAndTime(normalizedSub.endDate, "DD MMM yyyy")}
                  </p>
                )}
              </div>
            </div>
            {isAdmin && (
              <Row
                gutter={[16, 10]}
                justify={"space-between"}
                style={{ padding: "0 5px", paddingTop: 10 }}
              >
                {/* Button is now hidden for trials based on new rules */}
                {subscriptionState.isCancellable && (
                  <CustomButtonWithIcon
                    title="Cancel Subscription"
                    width={200}
                    onClick={() => setIsCancelModalVisible(true)}
                  />
                )}
                {normalizedSub?.type === "monthly" &&
                  // upgrade subscription → show upgrading yearly table

                  !normalizedSub?.cancellationDate && (
                    <CustomPrimaryButton
                      title={"Upgrade Subscription"}
                      width={normalizedSub?.isCancelled ? 200 : 150}
                      onClick={() => setUpgradePlanTableOpen(true)}
                      isLoading={loading}
                    />
                  )}

                {!isUserAlreadyOnStripe ? (
                  // First-time subscription → show pricing table
                  <CustomPrimaryButton
                    title="Subscribe Now"
                    width={200}
                    onClick={() => setIsPricingTableOpen(true)}
                    isLoading={loading}
                  />
                ) : normalizedSub?.isCancelled || normalizedSub?.isExpired ? (
                  // Existing user but subscription is cancelled or expired → show our DentalID table
                  <CustomPrimaryButton
                    title="Subscribe Now"
                    width={200}
                    onClick={() => setIsDentalIdPricingTableOpen(true)}
                    isLoading={loading}
                  />
                ) : null}
              </Row>
            )}
          </div>
        </Col>
        <Col xs={24} sm={24} md={24} lg={12} xl={8}>
          <div className="active_subscription_body">
            <div className="active_subscription_body_section">
              <div className="active_subscription_body_heading">
                <div className="_image">
                  <img src={dentalIcons.seatsIcon} alt="Seats Icon" />
                </div>
              </div>

              <div className="active_subscription_progress">
                <SeatsProgressBar
                  totalSeats={totalSeats}
                  activeSeats={activeSeats}
                />
              </div>
            </div>

            <div
              className="active_subscription_info_section"
              style={{ justifyContent: "space-between" }}
            >
              <div className="_seats_status_section">
                <p className="key">Seats in Plan</p>
                <p className="value">{totalSeats}</p>
              </div>
              {/* Button is now hidden for trials, expired, or cancelled plans */}
              {isAdmin && subscriptionState.isSeatUpdatable && (
                <CustomButtonWithIcon
                  title={"Update Seats"}
                  width={150}
                  onClick={() => setisUpdateSeats(true)}
                />
              )}
            </div>
            <div className="seats_count_section">
              <div className="_count_section">
                <p className="_title">Active Seats</p>
                <p className="_count">{activeSeats}</p>
              </div>
              <div className="_count_section">
                <p className="_title">Available Seats</p>
                <p className="_count">{availableSeats}</p>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* --- Modals remain unchanged --- */}
      <CustomModal
        open={isCancelModalVisible}
        onCancel={() => setIsCancelModalVisible(false)}
        width={560}
      >
        <CancelationModal
          onCancel={() => setIsCancelModalVisible(false)}
          onConfirm={onConfirm}
          isLoading={loading}
        />
      </CustomModal>

      <CustomModal
        open={isUpdateSeats}
        onCancel={() => setisUpdateSeats(false)}
        width={550}
      >
        <div className="update_seats_modal_section">
          <div className="update_seats_header">
            <div className="update_seats_icon">
              <img src={dentalIcons.seatsIcon} />
            </div>
            <div>
              <p className="_title">Update Seats</p>
            </div>
          </div>
          <div className="update_seats_modal_body">
            <p
              className="_subtitle"
              style={{
                fontSize: 15,
                color: "#666",
                marginTop: 4,
                marginBottom: 20,
              }}
            >
              You can adjust the total number of seats associated with your
              subscription. Use the controls below to increase or decrease the
              seat count as needed. This ensures accurate billing and access
              management for your team.
            </p>

            <div className="seats_info_section" style={{ height: 200 }}>
              {!seatsLoading && (
                <div className="_amount_info">
                  <ul>
                    {/* Always show current per-seat cost */}
                    <li>
                      You are currently paying <strong>${unit_price}</strong>{" "}
                      per seat.
                    </li>

                    {/* When seats are changing */}
                    {seatsInfo?.is_upgrade || seatsInfo?.is_downgrade ? (
                      <>
                        {/* Upgrade flow */}
                        {seatsInfo?.is_upgrade && (
                          <li>
                            You’ll be charged <strong>${pay_now_amount}</strong>{" "}
                            immediately for the additional seat(s).
                          </li>
                        )}

                        {/* Downgrade flow */}
                        {seatsInfo?.is_downgrade && (
                          <>
                            {Number(credit_applied_next_cycle) > 0 ? (
                              <li>
                                If you proceed, a credit of{" "}
                                <strong>${credit_applied_next_cycle}</strong>{" "}
                                will be applied to your next billing cycle based
                                on the reduced seat count. No charge or refund
                                will be issued today.
                              </li>
                            ) : (
                              <li>
                                Your subscription has already been paid for this
                                cycle. The seat change will apply starting from
                                the next cycle.
                              </li>
                            )}
                          </>
                        )}

                        {/* Renewal amount always shown if seats are changing */}
                        <li>
                          Your subscription will renew at{" "}
                          <strong>${total_due_next_cycle}</strong> starting from
                          the next cycle.
                        </li>
                      </>
                    ) : (
                      /* When no changes */
                      <>
                        <li>No changes will apply to your subscription.</li>
                        <li>
                          Your subscription will renew at{" "}
                          <strong>
                            $
                            {(
                              Number(unit_price) * seatsInfo?.old_quantity
                            ).toFixed(2)}
                          </strong>{" "}
                          starting from the next cycle.
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              )}
              {seatsLoading ? (
                <div
                  style={{
                    height: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Spin
                    indicator={<LoadingOutlined spin />}
                    size="large"
                    style={{ color: "#0A9FD4" }}
                  />
                </div>
              ) : null}
            </div>
            <br />
            <Row
              gutter={[10, 10]}
              style={{ gap: 10, margin: "20px 0" }}
              justify={"center"}
            >
              <CustomButtonWithIcon
                title="-"
                onClick={decreaseSeats}
                width={100}
                fontSize={30}
                disabled={seats === 1}
              />
              <p className="_seats">{seats}</p>
              <CustomButtonWithIcon
                title="+"
                onClick={increaseSeats}
                width={100}
                fontSize={30}
              />
            </Row>
            <br />
            <CustomPrimaryButton
              title="Update Seats"
              width={"100%"}
              onClick={onUpdateSeats}
              isLoading={loading}
              isDisbale={seatsLoading || seats === totalSeats}
            />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={upgradePlanTableOpen}
        onCancel={() => setUpgradePlanTableOpen(false)}
        width={760}
      >
        <UpgradePanModal
          onClose={() => setUpgradePlanTableOpen(false)}
          onRefetch={onRefetch}
        />
      </CustomModal>
      <CustomModal
        open={isPricingTableOpen}
        onCancel={() => setIsPricingTableOpen(false)}
        width={960}
      >
        <StripePricingTable />
      </CustomModal>

      <CustomModal
        open={isDentalIdPricingTableOpen}
        onCancel={() => setIsDentalIdPricingTableOpen(false)}
        width={900}
      >
        <DentalIDPricingTable
          onClose={() => setIsDentalIdPricingTableOpen(false)}
          onRefetch={onRefetch}
          seats={seats}
        />
      </CustomModal>
    </div>
  );
};

export default ActiveSubscription;
